import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClientContactSchema, ClientSchema } from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getData } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { ContactActionDialog, ContactDialog } from './ClientDetailPage';
import { CLIENTS } from './common';
import { MigrationTrace } from '../migration/MigrationTrace';
import { EmailLink, PhoneLinks } from '../../shared/ui/contact';

/**
 * One client contact at its own address (FRD-CORE-002; FRD-CRM-019 to CRM-023), where global
 * search and the client's page lead: its facts, its authoritative client (linked when the session
 * may read clients), Edit, Make primary and a confirmed Activate / Deactivate where the session
 * holds `crm.client_contact:change`, and its audit history. An unknown id reads as "no such
 * contact".
 */
export function ContactDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const canChange = hasCapability(session.data, 'crm.client_contact:change');
  const canSeeClient = hasCapability(session.data, 'crm.client:view');
  const [dialog, setDialog] = useState<'edit' | 'status' | 'primary' | null>(null);
  const contact = useQuery({
    queryKey: ['crm', 'contact', id],
    queryFn: () => getData(`/api/v1/crm/contacts/${id}`, ClientContactSchema),
  });
  const clientId = contact.data?.clientId ?? null;
  const client = useQuery({
    queryKey: ['crm', 'clients', 'one', clientId],
    enabled: canSeeClient && clientId !== null,
    queryFn: () => getData(`${CLIENTS}/${clientId ?? ''}`, ClientSchema),
  });
  const back =
    canSeeClient && clientId !== null ? (
      <Button component={RouterLink} to={`/crm/clients/${clientId}`} startIcon={<ArrowBackIcon />}>
        Back to the client
      </Button>
    ) : (
      <Button component={RouterLink} to="/crm/clients" startIcon={<ArrowBackIcon />}>
        All clients
      </Button>
    );
  if (contact.isPending) return <LoadingState label="Loading the contact" />;
  if (contact.isError) {
    return <RecordFailure title="Contact" noun="contact" error={contact.error} back={back} />;
  }
  const c = contact.data;
  const close = () => {
    setDialog(null);
    void queryClient.invalidateQueries({ queryKey: ['crm', 'contact', id] });
    void queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
  };
  const clientText =
    client.data === undefined ? 'Client' : `${client.data.code} — ${client.data.companyName}`;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={c.name}
        description="Client contact"
        actions={
          <>
            {back}
            {canChange && (
              <>
                {!c.isPrimary && c.status === 'active' && (
                  <Button
                    variant="outlined"
                    onClick={(event) => {
                      event.currentTarget.focus();
                      setDialog('primary');
                    }}
                  >
                    Make primary
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color={c.status === 'active' ? 'warning' : 'primary'}
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setDialog('status');
                  }}
                >
                  {c.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="contained"
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setDialog('edit');
                  }}
                >
                  Edit
                </Button>
              </>
            )}
          </>
        }
      />
      <Section title="Contact">
        <Facts
          label="Contact details"
          facts={[
            [
              'Client',
              canSeeClient ? (
                <RecordLink key="c" to={`/crm/clients/${c.clientId}`}>
                  {clientText}
                </RecordLink>
              ) : (
                'Not available to you'
              ),
            ],
            ['Role / designation', c.designation ?? '—'],
            ['Email', <EmailLink key="e" email={c.email} />],
            ['Phone', <PhoneLinks key="p" phone={c.phone} />],
            ['Primary contact', c.isPrimary ? 'Yes' : 'No'],
            ['Status', <StatusChip key="s" status={c.status} />],
          ]}
        />
      </Section>
      <MigrationTrace recordType="crm.client_contact" recordId={c.id} />
      <RecordHistory recordType="crm.client_contact" recordId={c.id} />
      {dialog === 'edit' && (
        <ContactDialog key={String(c.version)} clientId={c.clientId} contact={c} onClose={close} />
      )}
      {(dialog === 'status' || dialog === 'primary') && (
        <ContactActionDialog clientId={c.clientId} kind={dialog} contact={c} onClose={close} />
      )}
    </Stack>
  );
}
